import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  AbstractControlOptions,
  UntypedFormGroup,
  ValidatorFn,
} from '@angular/forms';
import { Subscription } from 'rxjs';

/**
 * Edits one underlying quantity as BOTH an absolute amount and a percentage of some reference
 * value - e.g. a fill-detect volume shown in gal alongside its percentage of a tank's capacity
 * limit. Both inputs render side by side under a single shared label and stay in sync: typing in
 * either one recalculates the other.
 *
 * The two halves are backed by two separate controls on the caller's form group
 * (valueControlName / percentControlName), so callers read whichever one their API expects
 * straight off the group as usual.
 *
 * `baseValue` is only consulted when converting at edit time. Changing it does NOT retroactively
 * rewrite either control - callers stay in charge of what happens when the reference moves
 * (e.g. flagging the fields for review rather than silently re-deriving them).
 */
@Component({
  selector: 'app-value-percent-field',
  templateUrl: './value-percent-field.component.html',
  styleUrls: ['./value-percent-field.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValuePercentFieldComponent implements AfterViewInit, OnDestroy {
  @Input() formGroup: UntypedFormGroup;
  @Input() fieldLabel: string;
  @Input() fieldId: string;

  /** Control holding the absolute amount (e.g. gal). */
  @Input() valueControlName: string;
  /** Control holding the percentage of baseValue. */
  @Input() percentControlName: string;

  /** The reference the percentage is "of" (e.g. capacity limit). */
  @Input() baseValue: number = 0;

  @Input() valueSuffix: string | null = null;
  @Input() percentSuffix: string | null = 'General.Pct';

  @Input() valueFormState: any = null;
  @Input() percentFormState: any = null;

  @Input() valueValidators: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null = null;
  @Input() percentValidators: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null = null;

  @Input() isRequired: boolean = false;
  @Input() appearance: 'legacy' | 'standard' | 'fill' | 'outline' = 'outline';
  @Input() warningText: string | null = null;
  /** Decimal places both halves are rounded to when derived from the other. */
  @Input() decimals: number = 2;

  /** Tab index for the value input; the percent input takes the next one. */
  @Input() tabIndex: number | null = null;

  /** Emitted after the user edits the absolute half and the percent has been recalculated. */
  @Output() valueEdited = new EventEmitter<void>();
  /** Emitted after the user edits the percent half and the absolute value has been recalculated. */
  @Output() percentEdited = new EventEmitter<void>();

  /** True when EITHER half is showing an error. The two halves are one quantity, so a rule broken
   *  in either unit (e.g. "percent cannot exceed 100%") is flagged on both inputs - otherwise the
   *  gal half looks fine while the percent half is red, for the same underlying problem. */
  public pairInvalid = false;
  private statusSub?: Subscription;

  constructor(private changeDetectorRef: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    //the inner app-form-fields register their controls in their own ngOnInit, so they exist by now
    const valueCtrl = this.formGroup?.get(this.valueControlName);
    const percentCtrl = this.formGroup?.get(this.percentControlName);
    if (!valueCtrl || !percentCtrl) return;

    this.statusSub = new Subscription();
    this.statusSub.add(valueCtrl.statusChanges.subscribe(() => this.refreshPairInvalid()));
    this.statusSub.add(percentCtrl.statusChanges.subscribe(() => this.refreshPairInvalid()));
    //defer the first pass so we don't write during the view-init CD cycle
    setTimeout(() => this.refreshPairInvalid());
  }

  ngOnDestroy(): void {
    this.statusSub?.unsubscribe();
  }

  private refreshPairInvalid(): void {
    const valueCtrl = this.formGroup?.get(this.valueControlName);
    const percentCtrl = this.formGroup?.get(this.percentControlName);
    const next = this.isShowingError(valueCtrl) || this.isShowingError(percentCtrl);
    if (next === this.pairInvalid) return;

    this.pairInvalid = next;
    this.changeDetectorRef.markForCheck();
  }

  /** Matches the error-state rule the inner fields use for their own red styling: a control is
   *  only flagged once the user has actually edited or visited it. Without the dirty/touched gate
   *  a half sits red for the whole of the initial load - the controls are created before the form
   *  data arrives, so a required half is briefly empty and therefore invalid through no fault of
   *  the user. Callers that need the styling up front (e.g. the capacity-change prompt) mark the
   *  controls touched, which is exactly what that gate looks for. */
  private isShowingError(ctrl: AbstractControl | null): boolean {
    return !!(ctrl?.invalid && ctrl.enabled && (ctrl.dirty || ctrl.touched));
  }

  get percentTabIndex(): number | null {
    return this.tabIndex == null ? null : this.tabIndex + 1;
  }

  /** Bound to both keyup (live, while typing) and change (commit, so paste/autofill also sync).
   *  Running twice for one edit is harmless - the conversion is idempotent. */
  onValueKeyup(): void {
    this.syncPercentFromValue();
    this.valueEdited.emit();
    this.changeDetectorRef.markForCheck();
  }

  onPercentKeyup(): void {
    this.syncValueFromPercent();
    this.percentEdited.emit();
    this.changeDetectorRef.markForCheck();
  }

  /** percent = value / base * 100. Only ever writes the half the user is NOT typing in. */
  private syncPercentFromValue(): void {
    const base = this.usableBase();
    const valueCtrl = this.formGroup?.get(this.valueControlName);
    const percentCtrl = this.formGroup?.get(this.percentControlName);
    if (!base || !valueCtrl || !percentCtrl) return;

    const value = this.parseEditedValue(valueCtrl.value);
    if (value === null) return;

    //emits, so cross-field validators watching this control still re-run
    percentCtrl.setValue(((value / base) * 100).toFixed(this.decimals));
  }

  /** value = percent / 100 * base. Only ever writes the half the user is NOT typing in. */
  private syncValueFromPercent(): void {
    const base = this.usableBase();
    const valueCtrl = this.formGroup?.get(this.valueControlName);
    const percentCtrl = this.formGroup?.get(this.percentControlName);
    if (!base || !valueCtrl || !percentCtrl) return;

    const percent = this.parseEditedValue(percentCtrl.value);
    if (percent === null) return;

    valueCtrl.setValue(((percent / 100) * base).toFixed(this.decimals));
  }

  /** Number() maps null, '' and whitespace to 0 rather than NaN, so an isNaN guard alone lets a
   *  mid-edit field write a spurious 0 into the other half - a native number input reports ''
   *  for a partially typed value like "12." or "1e". Returns null for anything not a real number.
   */
  private parseEditedValue(raw: any): number | null {
    if (raw === null || raw === undefined || String(raw).trim() === '') return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /** A base of 0 (or junk) makes the conversion meaningless - skip rather than write NaN/Infinity. */
  private usableBase(): number {
    const base = Number(this.baseValue);
    return Number.isFinite(base) && base > 0 ? base : 0;
  }
}
